import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import CommonSelect from "../../../core/common/commonSelect";
import { all_routes } from "../../router/all_routes";
import { useFeeType } from "../hooks/useFeeType";
import { FeeTypePayloadType } from "../types/feeTypeRequest";
import { showToast } from "../../../core/utils/alert";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller } from "react-hook-form";

const validationSchema = yup.object().shape({
  type: yup.string().required("Fee type is required"),
  status: yup.string().required("Status is required"),
  description: yup.string().required("Description is required"),
});

const AddFeeType = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // const { feeTypes, updateFeeType, addFeeType } = useFeeConfig();
  const { addNewFeeType, updateFeeType, useGetFeeTypeById } = useFeeType();
  const isEditMode = Boolean(id);

  const [feeType, setFeeType] = useState<FeeTypePayloadType>({
    type: "",
    status: "Active",
    description: "",
  });
  const {
    feeType: existingFeeType,
    isLoading,
    isError,
  } = useGetFeeTypeById(id || "");

  const feeTypeOptions = [
    { value: "registrationfee", label: "Registration Fee" },
    { value: "coursefee", label: "Course Fee" },
    { value: "busfee", label: "Bus Fee" },
    { value: "libraryfee", label: "Library Fee" },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
    setValue,
    watch,
  } = useForm<FeeTypePayloadType>({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: feeType,
  });

  useEffect(() => {
    if (existingFeeType) {
      reset({
        type: existingFeeType.type,
        status: existingFeeType.status,
        description: existingFeeType.description,
      });
    }
  }, [existingFeeType, reset]);

  const onSubmit = async (data: FeeTypePayloadType) => {
    try {
      if (id) {
        const result = await updateFeeType(id, data);
        if (result.success) {
          showToast({
            title: "Success",
            text: "Fee type updated successfully",
            icon: "success",
          });
          navigate(all_routes.feeTypes);
        } else {
          showToast({
            title: "Error",
            text: result.error || "Could not update the fee type",
            icon: "error",
          });
        }
      } else {
        const result = await addNewFeeType(data);
        if (result.success) {
          showToast({
            title: "Success",
            text: "Fee type added successfully",
            icon: "success",
          });
          navigate(all_routes.feeTypes);
        } else {
          showToast({
            title: "Error",
            text: result.error || "Could not add the fee type",
            icon: "error",
          });
        }
      }
    } catch (error) {
      console.error("Failed to save fee type:", error);
      showToast({
        title: "Error",
        text: "Failed to save fee type",
        icon: "error",
      });
    }
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Show loading state if data is not yet available
  if (isLoading) {
    return <div>Loading...</div>; // You can customize this loading state
  }

  // Show error state if there was an error fetching the fee type
  if (isError) {
    return <div>Error fetching fee type</div>; // Display error message
  }

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">
              {isEditMode ? "Edit Fee Type" : "Add Fee Type"}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.feeTypes}>Fee Types</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? "Edit Fee Type" : "Add Fee Type"}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Fee Type Form */}
        <div className="row">
          <div className="col-md-12">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-file-invoice fs-16" />
                    </span>
                    <h4 className="text-dark">Fee Type Details</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-6">
                      {/* <div className="mb-3">
                        <label className="form-label">Fee Type <span className="text-danger">*</span></label>
                        <input
                          {...register("type")}
                          className={`form-control ${errors.type ? 'is-invalid' : ''}`}
                          placeholder="Enter fee type"
                        />
                        {errors.type && <div className="invalid-feedback">{errors.type.message}</div>}
                      </div> */}
                      <div className="mb-3">
                        <label className="form-label">
                          Fee Type <span className="text-danger">*</span>
                        </label>
                        <Controller
                          name="type"
                          control={control}
                          render={({ field }) => (
                            <CommonSelect
                              options={feeTypeOptions}
                              value={feeTypeOptions.find(
                                (opt) => opt.value === field.value
                              )}
                              onChange={(option) =>
                                field.onChange(option?.value || "")
                              }
                              className={`select ${
                                errors.type ? "is-invalid" : ""
                              }`}
                            />
                          )}
                        />
                        {errors.type && (
                          <div className="invalid-feedback">
                            {errors.type.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Status <span className="text-danger">*</span>
                        </label>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <CommonSelect
                              options={statusOptions}
                              value={statusOptions.find(
                                (opt) => opt.value === field.value
                              )}
                              onChange={(option) =>
                                field.onChange(option?.value || "")
                              }
                              className={`select ${
                                errors.status ? "is-invalid" : ""
                              }`}
                            />
                          )}
                        />
                        {errors.status && (
                          <div className="invalid-feedback">
                            {errors.status.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Description <span className="text-danger">*</span>
                        </label>
                        <textarea
                          {...register("description")}
                          className={`form-control ${
                            errors.description ? "is-invalid" : ""
                          }`}
                          rows={4}
                          placeholder="Enter fee type description"
                        />
                        {errors.description && (
                          <div className="invalid-feedback">
                            {errors.description.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="text-end">
                <Link to={all_routes.feeTypes} className="btn btn-light me-3">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary">
                  {isEditMode ? "Update Fee Type" : "Save Fee Type"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFeeType;
